import React, { createContext, useEffect, useContext, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import uuid from 'utils/uuid';
import store from 'utils/store';
import produce from 'utils/produce';
import uniq from 'utils/uniq';
import { getRandomColor } from 'utils/color';
import useModalContext from 'components/context/modal';
import useDataContext from 'data/context/data';
import { getRelatedProductsIds } from 'data/lib/product';
import db, { useCartDatabase } from '../lib/database';

//
// CartContext History
//
// v6 - store contains key 'items' - it's hash of products { [id]: cartItem }
// v7 - all items are saved in the IndexedDB. Store contains only promoCode

const DEFAULT_VALUE = {
  promoCode: '',
  version: 7,
};

const generateCartItem = (
  cartItemId,
  product,
  version,
  data,
  editable = true,
  quantity = 1,
  bundleData = {},
  draftId = undefined,
  draftName = undefined,
  draftLastModified = undefined,
) => {
  let name = product.name;
  if (product.display && data && data.options) {
    name = `${product.name} (${Object.entries(data.options)
      .map(([, value]) => value)
      .join(', ')})`;
  }

  // BUNDLE
  let bundle;
  if (bundleData?.bundle) {
    // when product needs to choose between many secondary items, it's necessary to check related products
    const secondaryProductIds = bundleData.bundle.secondaryProducts.map(({ id }) => id);
    const possibleRelatedProductIds = getRelatedProductsIds(product, data);

    const intersects = secondaryProductIds.filter(id => possibleRelatedProductIds.indexOf(id) >= 0);

    const secondaryProductIndex = secondaryProductIds.findIndex(id => intersects.indexOf(id) >= 0);

    // when I'm sure what to choose - bundle is  created
    if (secondaryProductIndex >= 0) {
      bundle = {
        id: bundleData.bundle.id,
        secondaryProducts: bundleData.bundle.secondaryProducts,
        secondaryProductIndex,
      };
    } else if (bundleData.bundle.secondaryProducts.length === 1) {
      // when secondaryProducts has only one item and it's not related product - it's also OK to create bundle
      const allRelatedProductIds = getRelatedProductsIds(product);
      if (allRelatedProductIds.indexOf(bundleData.bundle.secondaryProducts[0].id) < 0) {
        bundle = {
          id: bundleData.bundle.id,
          secondaryProducts: bundleData.bundle.secondaryProducts,
          secondaryProductIndex: 0,
        };
      }
    }

    if (bundle) {
      name = bundleData.bundle.name;
    }
  }

  // UPSELL
  let upsell;
  if (bundleData?.upsell) {
    upsell = bundleData.upsell;
  }

  // GROUP COLOR - RELATED TO UPSELL
  let group;
  if (upsell) {
    group = getRandomColor().toHex();
  }

  return {
    id: cartItemId,
    productId: product.id,
    version,
    draftId: draftId ? parseInt(draftId, 10) : undefined,
    draftName,
    draftLastModified,
    name,
    quantity,
    data,
    image: undefined, // TODO when we want to show image based on data - use this key
    relatedIds: getRelatedProductsIds(product, data),
    editable: editable && !product.display,
    bundle,
    upsell,
    group,
  };
};

const isQuantityChangeEnabled = sku => {
  if (sku.indexOf('giftcard-') >= 0) {
    return false;
  }

  return true;
};

const isVisibleInTheCart = sku => {
  if (sku === 'giftnote') {
    return false;
  }

  return true;
};

const CartContext = createContext();

export const CartContextProvider = props => {
  const modalContext = useModalContext();
  const dataContext = useDataContext();

  const [isCartStoreLoading, setIsCartStoreLoading] = useState(true);
  const [cartStore, setCartStore] = useState();

  const showNotSupported = () => {
    modalContext.confirm(
      <>Please use the latest version of Chrome, Opera, Firefox, Safari or Edge.</>,
      {
        title: 'Your browser is not currently supported',
        closeable: false,
      },
    );
  };

  //
  // CartStore init
  //
  useEffect(() => {
    db.open()
      .then(() => {
        console.log('indexeddb_supported');
      })
      .catch(e => {
        showNotSupported();
        console.error('IndexedDB - Error!');
        console.error(e);
        props.onCustomEvent('indexeddb_unsupported');
      });

    const value = store.getCompressedJSON(props.storageKey, DEFAULT_VALUE);
    // Actual version
    if (value.version === DEFAULT_VALUE.version) {
      setCartStore(value);
      setIsCartStoreLoading(false);
      return;
    }

    // Older version - migrations
    if (DEFAULT_VALUE.version === 7 && value.version === 6) {
      if (Object.keys(value.items ?? {}).length > 0) {
        db.items
          .bulkAdd(
            Object.entries(value.items).map(([id, item]) => ({
              id,
              ...item,
            })),
          )
          .then(() => {
            // success - remove items from cartStore
            setCartStore({ ...value, version: 7, items: undefined });
            setIsCartStoreLoading(false);

            console.log('CartContext - Successfully migrated to IndexedDB!');
            props.onCustomEvent('indexeddb_cart_migration_succeeded');
          })
          .catch(e => {
            // something went wrong - keep items in cartStore
            setCartStore({ ...value, version: 7 });
            setIsCartStoreLoading(false);
            showNotSupported();

            console.error('CartContext - issue with migration to IndexedDB!');
            console.error(e);
            window.Rollbar?.error(`CartContext - issue with migration to IndexedDB!`);
            props.onCustomEvent('indexeddb_cart_migration_failed');
          });
        return;
      }
    }

    // Some other older version without migration
    store.remove(props.storageKey);
    setCartStore(DEFAULT_VALUE);
    setIsCartStoreLoading(false);
  }, []);

  //
  // CartStore save
  //
  useEffect(() => {
    if (cartStore) {
      try {
        store.setCompressedJSON(props.storageKey, cartStore, true);
      } catch (e) {
        console.error({
          'Number of LocalStorage keys': store.size(),
          'LocalStorage keys': store.keys(),
          Error: e?.message,
        });
        window.Rollbar?.error(`LocalStorageError - "${props.storageKey}" (compressed)`);
      }
    }
  }, [cartStore]);

  //
  // Load items from database
  //
  const { isLoading: isDatabaseLoading, data: items } = useCartDatabase(
    () => db.items.toArray(),
    [],
    [],
    value => {
      return value.reduce((acc, item) => {
        const hasUpsell = !!item.upsell;

        acc.push({
          product: dataContext.getSkuProduct(item.productId) ?? {},
          quantityChangeEnabled: !hasUpsell && isQuantityChangeEnabled(item.productId),
          visibleInTheCart: isVisibleInTheCart(item.productId),
          hasUpsell,
          ...item,
        });

        if (hasUpsell) {
          const { upsell } = item;
          acc.push({
            product: dataContext.getSkuProduct(upsell.id) ?? {},
            id: [item.id, upsell.id].join('_'),
            productId: upsell.id,
            version: upsell.version,
            name: upsell.name,
            data: upsell.data,
            quantity: upsell.quantity ?? item.quantity,
            editable: false,
            deletable: false,
            quantityChangeEnabled: false,
            visibleInTheCart: true,
            isUpsell: true,
            group: item.group,
          });
        }

        return acc;
      }, []);
    },
  );

  //
  // relatedIds
  //
  const relatedIds = useMemo(() => {
    return uniq(items.reduce((acc, item) => [...acc, ...(item.relatedIds ?? [])], []));
  }, [items]);

  //
  // METHODS
  //

  const getItem = id => {
    return items.find(item => item.id === id);
  };

  const exists = id => {
    return !!getItem(id);
  };

  const setPromoCode = promoCode => {
    setCartStore(value =>
      produce(value, draft => {
        draft.promoCode = promoCode;
      }),
    );
  };

  const setShippingType = shippingType => {
    setCartStore(value =>
      produce(value, draft => {
        draft.shippingType = shippingType;
      }),
    );
  };

  const setShippingAddress = shippingAddress => {
    setCartStore(value =>
      produce(value, draft => {
        draft.shippingAddress = shippingAddress;
      }),
    );
  };

  const updateItemQuantity = (id, diff = 1) => {
    db.items.where({ id }).modify((value, ref) => {
      ref.value.quantity = value.quantity + diff;
    });
  };

  const removeItem = id => {
    modalContext.confirm('Are you sure you want to remove this item from your cart?', {
      title: 'Remove Product?',
      onConfirm: () => {
        db.items.where({ id }).delete();
      },
    });
  };

  //
  // Apply promocode through props
  //
  useEffect(() => {
    if (!isCartStoreLoading && props.promoCode) {
      setPromoCode(props.promoCode);
      modalContext.toast(
        <>
          The coupon <strong>{props.promoCode.toUpperCase()}</strong> is in your cart. Make your
          prints to get those savings.
        </>,
        { type: 'success' },
      );
    }
  }, [props.promoCode, isCartStoreLoading]);

  //
  // isLoading depends on database and localStorage
  //
  const isLoading = useMemo(() => isCartStoreLoading || isDatabaseLoading, [
    isCartStoreLoading,
    isDatabaseLoading,
  ]);

  return (
    <CartContext.Provider
      value={useMemo(
        () => ({
          isLoading,

          items,

          relatedIds,

          length: items.filter(item => item.visibleInTheCart).length,

          promoCode: cartStore?.promoCode ?? undefined,

          shippingType: cartStore?.shippingType ?? undefined,

          shippingAddress: cartStore?.shippingAddress ?? undefined,

          trackEvent(action, payload = {}) {
            props.onCustomEvent(action, payload);
          },

          exists,

          getItem,

          getItemBySku(sku) {
            return items.find(item => item.productId === sku);
          },

          addItem(
            product,
            version,
            data = undefined,
            editable = true,
            bundle = {},
            draftId = undefined,
            draftName = undefined,
            draftLastModified = undefined,
            quantity = 1,
          ) {
            let id;
            if (product.display) {
              if (data) {
                id = `${product.id}-${JSON.stringify(data)}`;
              } else if (bundle?.bundle || bundle?.upsell) {
                id = `${product.id}-${uuid()}`;
              } else {
                id = product.id;
              }
            } else {
              id = uuid();
            }

            if (exists(id)) {
              updateItemQuantity(id, 1);
            } else {
              db.items.add(
                generateCartItem(
                  id,
                  product,
                  version,
                  data,
                  editable,
                  quantity,
                  bundle,
                  draftId,
                  draftName,
                  draftLastModified,
                ),
              );
            }

            props.onCartItemAdd(product);

            return id;
          },

          editItem(
            id,
            product,
            version,
            data = undefined,
            editable = true,
            bundle = {},
            draftId = undefined,
            draftName = undefined,
            draftLastModified = undefined,
            quantity = undefined,
          ) {
            db.items.where({ id }).modify((value, ref) => {
              ref.value = generateCartItem(
                id,
                product,
                version,
                data,
                editable,
                quantity ?? value.quantity,
                bundle,
                draftId,
                draftName,
                draftLastModified,
              );
            });

            return id;
          },

          updateData(id, data) {
            db.items.where({ id }).modify((value, ref) => {
              ref.value.data = data;
            });
          },

          changeItemQuantity(item, diff) {
            if (diff > 0) {
              // increment
              updateItemQuantity(item.id, diff);
            } else if (item.quantity > Math.abs(diff)) {
              // decrement
              updateItemQuantity(item.id, diff);
            } else {
              // remove
              removeItem(item.id);
            }
          },

          removeItem,

          removeSku(sku) {
            db.items.where({ productId: sku }).delete();
          },

          upgradeItem(id, product) {
            db.items.where({ id }).modify((value, ref) => {
              ref.value = {
                ...value,
                ...generateCartItem(
                  id,
                  product,
                  value.version,
                  value.data,
                  value.editable,
                  value.quantity,
                  {},
                  value.draftId,
                  value.draftName,
                  value.draftLastModified,
                ),
              };
            });

            props.onCartItemUpgrade(product);
          },

          clear() {
            setCartStore(DEFAULT_VALUE);
            db.items.clear();
          },

          setPromoCode,

          setShippingType,

          setShippingAddress,

          removePromoCode() {
            setPromoCode('');
          },
        }),
        [isLoading, cartStore, items],
      )}
    >
      {props.children}
    </CartContext.Provider>
  );
};

CartContextProvider.propTypes = {
  storageKey: PropTypes.string,
  promoCode: PropTypes.string,
  onCartItemAdd: PropTypes.func,
  onCartItemUpgrade: PropTypes.func,
  onCustomEvent: PropTypes.func,
  children: PropTypes.node,
};

CartContextProvider.defaultProps = {
  storageKey: 'cart_next',
  onCartItemAdd: () => {},
  onCartItemUpgrade: () => {},
  onCustomEvent: () => {},
};

export const errorHandler = (cartContext, error) => {
  let showError = true;
  switch (error) {
    case 'The product giftnote cannot be purchased because it cannot be printed separately.':
      showError = false;
      cartContext.removeSku('giftnote');
      break;

    case 'The selected shipping is not available for this country.':
      showError = false;
      cartContext.setShippingType(null);
      break;

    default:
      showError = true;
      break;
  }
  return showError;
};

const useCartContext = () => {
  return useContext(CartContext);
};

export default useCartContext;
